import { Stack } from "@mui/material";
import React, { useCallback, useMemo } from "react";

import { SkeletonIndicator } from "components/common/AnimatedWebcam/SkeletonIndicator";
import { SimpleProjectedAccuracy } from "components/detector/InfoChart/ProjectedMLAcc/ProjectedAccuracy";
import { AnswerType, useTopLevelStats, type MetricClassCounts } from "hooks/DetectorDetail/useTopLevelStats";
import { DetectorModes } from "types/createDetector";
import { ANSWER_VALUES } from "types/imageQueries";
import { FilterOptionsLabel, getFilterOptions, redirectToDataPage } from "utils/redirectDataPage";

import { DetectorMetricsTable, DetectorMetricsTableRowData } from "../DetectorMetricsTable/DetectorMetricsTable";
import { DetectorTableCell } from "../DetectorMetricsTable/types";

import { type TopLevelStatsProps } from "./props";
import { StatsSelectors } from "./StatsSelectors";

// This is also used in DetectorItem.tsx
export function generateArrayWithMax(maxCount: number) {
  const arr = Array.from({ length: maxCount + 1 }, (_, i) => String(i));
  arr.push(`>${maxCount}`);
  arr.push("UNCLEAR");

  return arr;
}

export function CountTopLevelStats(props: TopLevelStatsProps) {
  const { detectorData, confidenceThreshold, count } = props;
  const { answerType, handleActivityPeriodChange, handleAnswerTypeChange, loadingMetrics, metrics, period, status } =
    useTopLevelStats({
      detectorData,
      confidenceThreshold,
      count,
    });

  const METRICS_COLUMNS = useMemo(
    () =>
      detectorData.mode === DetectorModes.COUNT && metrics?.summary?.max_count
        ? generateArrayWithMax(metrics?.summary?.max_count)
        : [],
    [detectorData.mode, metrics?.summary?.max_count]
  );
  const columns = useMemo(() => [...METRICS_COLUMNS, "TOTAL"], [METRICS_COLUMNS]);

  const getRowColumns = useCallback(
    (metric: MetricClassCounts | undefined, label: FilterOptionsLabel) => {
      if (!metric) return [];
      const columns: DetectorTableCell[] = METRICS_COLUMNS.map((key) => ({
        key,
        link: redirectToDataPage({
          detectorId: detectorData.id,
          filterOptions: getFilterOptions({ label, answerType, value: key, period, mode: DetectorModes.COUNT }),
        }),
        value: metric[key] ?? 0,
        className: key !== ANSWER_VALUES.UNCLEAR ? "text-primary-light" : "text-neutral-400",
      }));
      columns.push({
        key: "TOTAL",
        link: redirectToDataPage({
          detectorId: detectorData.id,
          filterOptions: getFilterOptions({
            label,
            answerType,
            value: label === "Total Answered" ? "true" : undefined,
            period,
            mode: DetectorModes.COUNT,
          }),
        }),
        value: Object.keys(metric).reduce((total, m) => (metric[m] ?? 0) + total, 0),
      });
      return columns;
    },
    [METRICS_COLUMNS, answerType, detectorData.id, period]
  );

  const generateValuesRow = useCallback(
    (label: FilterOptionsLabel, metric: MetricClassCounts) => ({
      label,
      cells: [...getRowColumns(metric, label)],
    }),
    [getRowColumns]
  );

  const rowsData: DetectorMetricsTableRowData[] = useMemo(() => {
    if (!metrics) return [];

    return [
      generateValuesRow("Answered By ML", metrics.summary.class_counts.source_ml),
      generateValuesRow("Answered By Cloud Labelers", metrics.summary.class_counts.cloud),
      generateValuesRow("Ground Truth", metrics.summary.class_counts.cust_labeler),
      {
        ...generateValuesRow("Total Answered", metrics.summary.class_counts.total),
        className: "border-b-2 border-gray-500 border-solid",
        textClass: "font-bold",
      },
      {
        label: answerType === AnswerType.FIRST_ANSWER ? "No First Answer" : "Not Answered",
        className: "border-b-2 border-gray-200 border-solid",
        textClass: "text-neutral-400",
        colSpan: columns.length,
        cell: {
          key: "not-answered-total",
          value: metrics?.summary?.unconfident_counts?.total || 0,
          link: redirectToDataPage({
            detectorId: detectorData.id,
            filterOptions: getFilterOptions({
              label: "Not Answered",
              answerType,
              value: "false",
              period,
              mode: DetectorModes.COUNT,
            }),
          }),
        },
      },
      {
        label: "Total queries",
        className: "border-b-2 border-gray-500 border-solid",
        textClass: "font-bold",
        colSpan: columns.length,
        cell: {
          key: "not-answered-total",
          value: metrics?.summary?.total_iqs || 0,
          link: redirectToDataPage({
            detectorId: detectorData.id,
            filterOptions: getFilterOptions({ period }),
          }),
        },
      },
    ];
  }, [metrics, columns, generateValuesRow, answerType, detectorData.id, period]);

  if (status) return status;

  return (
    <Stack className="w-full gap-4">
      {!detectorData ? (
        SkeletonIndicator({ loading: true, height: 58 })
      ) : (
        <SimpleProjectedAccuracy detectorData={detectorData} />
      )}
      <StatsSelectors
        period={period}
        handleActivityPeriodChange={handleActivityPeriodChange}
        answerType={answerType}
        handleAnswerTypeChange={handleAnswerTypeChange}
      />
      {loadingMetrics ? (
        SkeletonIndicator({ loading: true, height: 295, className: "mt-4 mb-5" })
      ) : (
        <DetectorMetricsTable columns={columns} rowsData={rowsData} maxCount={10} />
      )}
    </Stack>
  );
}
